"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import { useSeizeSettingsOptional } from "@/contexts/SeizeSettingsContext";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { amIUser } from "@/helpers/Helpers";
import { getWavePathRoute, getWaveRoute } from "@/helpers/navigation.helpers";
import { MEMES_SEEKING_NOMINATION_WAVE_ID } from "@/helpers/waves/memes-nomination";
import { ArrowRightIcon, ArrowUpTrayIcon, ChatBubbleLeftRightIcon, CheckCircleIcon, PlusIcon, SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { useMemo, useState, type ReactNode } from "react";
import { clearIdentityGettingStartedSession, hasIdentityGettingStartedSession } from "./identityGettingStartedSession";
interface IdentityGettingStartedCardProps {
  readonly profile: ApiIdentity;
  readonly className?: string | undefined;
}
interface NextStepAction {
  readonly title: string;
  readonly description?: string;
  readonly href: string;
  readonly icon: ReactNode;
}
const CREATE_WAVE_HREF = "/waves?create=wave";
const COMMUNITY_WAVES_HREF = "/waves";
const NOMINATION_WAVE_HREF = getWavePathRoute(MEMES_SEEKING_NOMINATION_WAVE_ID);
const getProfileHandle = (profile: ApiIdentity): string | null => {
  const handle = profile.handle?.trim();
  if (!handle) {
    return null;
  }
  return handle;
};
function IdentityGettingStartedPrimaryAction(t0) {
  const $ = _c(5);
  const {
    href
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="tw-flex tw-size-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-text-black md:tw-hidden lg:tw-flex"><SparklesIcon className="tw-size-4" /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">{t1}<span className="tw-min-w-0"><span className="tw-block tw-text-sm tw-font-bold tw-leading-5 tw-text-black">Explore the Main Stage</span></span></span>;
    t3 = <ArrowRightIcon className="tw-size-4 tw-flex-shrink-0 tw-text-black tw-transition-transform tw-duration-300 desktop-hover:group-hover/main:tw-translate-x-1" />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== href) {
    t4 = <Link href={href} className="tw-group/main tw-inline-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-bg-iron-100 tw-px-4 tw-py-2.5 tw-text-left tw-text-black tw-no-underline tw-shadow-[0_0_24px_rgba(255,255,255,0.08)] tw-transition-all tw-duration-300 hover:-tw-translate-y-0.5 hover:tw-bg-iron-300 hover:tw-text-black hover:tw-no-underline hover:tw-shadow-[0_0_26px_rgba(255,255,255,0.1)] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 sm:tw-w-fit sm:tw-min-w-60">{t2}{t3}</Link>;
    $[3] = href;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function IdentityGettingStartedSecondaryAction(t0) {
  const $ = _c(16);
  const {
    action
  } = t0;
  let t1;
  if ($[0] !== action.icon) {
    t1 = <span className="tw-flex tw-size-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white/[0.05] tw-bg-white/[0.03] tw-text-iron-300 tw-shadow-sm tw-transition-colors desktop-hover:group-hover/item:tw-bg-white/[0.06] desktop-hover:group-hover/item:tw-text-white md:tw-bg-white/[0.04]">{action.icon}</span>;
    $[0] = action.icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== action.title) {
    t2 = <span className="tw-block tw-text-sm tw-font-semibold tw-leading-5 tw-text-iron-200 tw-transition-colors desktop-hover:group-hover/item:tw-text-white">{action.title}</span>;
    $[2] = action.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== action.description) {
    t3 = <span className="tw-mt-1 tw-hidden tw-text-xxs tw-leading-4 tw-text-iron-500 tw-transition-colors desktop-hover:group-hover/item:tw-text-iron-400 lg:tw-block">{action.description}</span>;
    $[4] = action.description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="tw-min-w-0 tw-pr-1">{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">{t1}{t4}</span>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ArrowRightIcon className="tw-mr-1 tw-size-4 tw-flex-shrink-0 tw-text-iron-600 tw-transition-all tw-duration-300 desktop-hover:group-hover/item:tw-translate-x-0.5 desktop-hover:group-hover/item:tw-text-white" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== action.href || $[14] !== t5) {
    t7 = <Link href={action.href} className="tw-group/item tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-transparent tw-bg-white/[0.02] tw-px-3 tw-py-2.5 tw-text-left tw-no-underline tw-transition-all tw-duration-200 hover:tw-border-white/[0.05] hover:tw-bg-white/[0.04] hover:tw-no-underline focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 active:tw-scale-[0.99] active:tw-bg-white/[0.06] md:tw-p-3">{t5}{t6}</Link>;
    $[13] = action.href;
    $[14] = t5;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function IdentityGettingStartedCardContent(t0) {
  const $ = _c(35);
  const {
    className,
    onDismiss
  } = t0;
  const settings = useSeizeSettingsOptional();
  let t1;
  if ($[0] !== settings?.seizeSettings.memes_wave_id) {
    t1 = settings?.seizeSettings.memes_wave_id?.trim() ?? "";
    $[0] = settings?.seizeSettings.memes_wave_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mainStageWaveId = t1;
  let t2;
  if ($[2] !== mainStageWaveId) {
    t2 = mainStageWaveId ? getWaveRoute({
      waveId: mainStageWaveId,
      isDirectMessage: false,
      isApp: false
    }) : null;
    $[2] = mainStageWaveId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const mainStageHref = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      title: "Join conversations",
      description: "Connect with the community in active waves",
      href: COMMUNITY_WAVES_HREF,
      icon: <ChatBubbleLeftRightIcon className="tw-size-4" />
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      title: "Create a wave",
      description: "Start your own space and spark discussions",
      href: CREATE_WAVE_HREF,
      icon: <PlusIcon className="tw-size-4" />
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [t3, t4, {
      title: "Submit art",
      description: "Seek nomination to get featured on the Main Stage",
      href: NOMINATION_WAVE_HREF,
      icon: <ArrowUpTrayIcon className="tw-size-4" />
    }];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const secondaryActions = t5;
  if (!mainStageHref) {
    return null;
  }
  const t6 = `tw-group tw-relative ${className ?? ""}`;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="tw-pointer-events-none tw-absolute -tw-inset-2 tw-rounded-xl tw-bg-blue-500/10 tw-opacity-50 tw-blur-2xl tw-transition-opacity tw-duration-700 desktop-hover:group-hover:tw-opacity-75" />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-blue-600/15 tw-via-purple-500/5 tw-to-transparent" />;
    t9 = <div className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-h-px tw-w-full tw-bg-gradient-to-r tw-from-transparent tw-via-blue-400/40 tw-to-transparent" />;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="tw-relative tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-blue-400/25 tw-bg-blue-500/20 tw-shadow-[0_0_22px_rgba(59,130,246,0.24)]"><CheckCircleIcon className="tw-size-5 tw-text-cyan-200 tw-drop-shadow-[0_0_10px_rgba(34,211,238,0.65)]" /></div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <XMarkIcon className="tw-size-4 tw-flex-shrink-0" />;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== onDismiss) {
    t12 = <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">{t10}<button type="button" onClick={onDismiss} aria-label="Dismiss guidance" className="tw-flex tw-size-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/[0.05] tw-bg-white/[0.05] tw-text-iron-400 tw-transition-colors hover:tw-text-iron-200 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 active:tw-bg-white/[0.1] md:tw-hidden md:tw-size-8">{t11}</button></div>;
    $[12] = onDismiss;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <h3 className="tw-mb-1.5 tw-text-lg tw-font-bold tw-leading-6 tw-tracking-tight tw-text-white md:tw-text-2xl">Getting started</h3>;
    t14 = <p className="tw-mb-3 tw-max-w-[22rem] tw-text-xxs tw-font-medium tw-leading-5 tw-text-iron-400">Add an About statement to complete your profile, or dive right into the network.</p>;
    $[14] = t13;
    $[15] = t14;
  } else {
    t13 = $[14];
    t14 = $[15];
  }
  let t15;
  if ($[16] !== mainStageHref) {
    t15 = <IdentityGettingStartedPrimaryAction href={mainStageHref} />;
    $[16] = mainStageHref;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== t12 || $[19] !== t15) {
    t16 = <div className="tw-relative tw-flex tw-overflow-hidden tw-p-4 md:tw-w-[45%] lg:tw-p-6">{t8}{t9}<div className="tw-relative tw-z-10 tw-flex tw-w-full tw-flex-col tw-justify-center">{t12}{t13}{t14}{t15}</div></div>;
    $[18] = t12;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  let t18;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="tw-text-xs tw-font-semibold tw-leading-4 tw-transition-colors">Dismiss</span>;
    t18 = <XMarkIcon className="tw-size-3.5 tw-transition-colors" />;
    $[21] = t17;
    $[22] = t18;
  } else {
    t17 = $[21];
    t18 = $[22];
  }
  let t19;
  if ($[23] !== onDismiss) {
    t19 = <button type="button" onClick={onDismiss} aria-label="Dismiss guidance" className="tw-group/close tw-absolute tw-right-3.5 tw-top-3.5 tw-z-20 tw-hidden tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-white/[0.05] tw-bg-white/[0.03] tw-px-3 tw-py-1.5 tw-text-iron-400 tw-transition-all tw-duration-200 hover:tw-bg-white/[0.08] hover:tw-text-white focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 active:tw-scale-[0.97] md:tw-flex">{t17}{t18}</button>;
    $[23] = onDismiss;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <p className="tw-mb-3 tw-ml-1 tw-hidden tw-pr-24 tw-text-[0.625rem] tw-font-bold tw-uppercase tw-leading-4 tw-tracking-widest tw-text-iron-500 md:tw-block">More to explore</p>;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="tw-pt-2">{t20}<div className="tw-flex tw-flex-col tw-gap-1">{secondaryActions.map(_temp)}</div></div>;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== t19) {
    t22 = <div className="tw-relative tw-flex tw-flex-col tw-justify-center tw-bg-[#0A0F18] tw-p-3 md:tw-w-[55%] lg:tw-p-6">{t19}{t21}</div>;
    $[27] = t19;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== t16 || $[30] !== t22) {
    t23 = <div className="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-[#0E1420] tw-shadow-[0_20px_40px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] md:tw-flex-row">{t16}{t22}</div>;
    $[29] = t16;
    $[30] = t22;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] !== t23 || $[33] !== t6) {
    t24 = <section className={t6} aria-label="Getting started with your identity">{t7}{t23}</section>;
    $[32] = t23;
    $[33] = t6;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  return t24;
}
function _temp(action) {
  return <IdentityGettingStartedSecondaryAction key={action.title} action={action} />;
}
export default function IdentityGettingStartedCard(t0) {
  const $ = _c(6);
  const {
    profile,
    className
  } = t0;
  const {
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const {
    address
  } = useSeizeConnectContext();
  const profileHandle = getProfileHandle(profile);
  const [isDismissed, setIsDismissed] = useState(false);
  const isOwnDirectProfile = !!profileHandle && !activeProfileProxy && amIUser({
    profile,
    address,
    connectedHandle: connectedProfile?.handle ?? undefined
  });
  const hasCreationSession = hasIdentityGettingStartedSession(profileHandle);
  const isVisible = !isDismissed && hasCreationSession;
  let t1;
  if ($[0] !== profileHandle || $[1] !== setIsDismissed) {
    t1 = () => {
      clearIdentityGettingStartedSession(profileHandle);
      setIsDismissed(true);
    };
    $[0] = profileHandle;
    $[1] = setIsDismissed;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const dismiss = t1;
  if (!profileHandle || !isOwnDirectProfile || !isVisible) {
    return null;
  }
  let t2;
  if ($[3] !== className || $[4] !== dismiss) {
    t2 = <IdentityGettingStartedCardContent className={className} onDismiss={dismiss} />;
    $[3] = className;
    $[4] = dismiss;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}